import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogIn, KeyRound, ExternalLink } from "lucide-react";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { loginPilot, saveSession, SITE_PROFILE_URL } from "@/lib/api";

export default function Login() {
  const [apiKey, setApiKey] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!apiKey.trim()) {
      toast.error("Please paste your API key.");
      return;
    }
    setLoading(true);
    try {
      const data = await loginPilot(apiKey.trim());
      saveSession(apiKey.trim(), data.user, {
        is_admin: data.is_admin,
        admin_dashboard_url: data.admin_dashboard_url,
        profile_url: data.profile_url,
      });
      toast.success(`Welcome back, ${data.user?.name || "Pilot"}!`);
      navigate("/dashboard");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401) {
        toast.error(detail || "Invalid API key.");
      } else {
        toast.error(detail || "Unable to sign in right now. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Log In"
      subtitle="Sign in securely with your Air India Virtual API key."
      footer={
        <>
          New to the crew?{" "}
          <Link to="/register" data-testid="login-to-register-link" className="font-semibold text-[#D4AF37] hover:underline">
            Create Account
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="apiKey" className="flex items-center gap-2 text-sm text-[#FDFBF7]">
            <KeyRound className="h-4 w-4 text-[#D4AF37]" /> Pilot API Key
          </Label>
          <Input
            id="apiKey"
            data-testid="login-apikey-input"
            placeholder="Paste your API key"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            className="border-white/10 bg-white/[0.03] font-mono-data text-sm text-[#FDFBF7] placeholder:text-[#A1A1AA]/50 focus-visible:ring-[#8A1538]"
            autoComplete="off"
          />
          <p className="text-xs leading-relaxed text-[#A1A1AA]">
            Find your key on the main site under{" "}
            <a
              href={SITE_PROFILE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="login-profile-link"
              className="inline-flex items-center gap-1 font-semibold text-[#D4AF37] hover:underline"
            >
              Profile <ExternalLink className="h-3 w-3" />
            </a>{" "}
            → API Key.
          </p>
        </div>

        <button
          type="submit"
          data-testid="login-submit-btn"
          disabled={loading}
          className="flex w-full items-center justify-center gap-2 rounded-full bg-[#8A1538] px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#A81A45] disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
          {loading ? "Verifying key…" : "Log In"}
        </button>
      </form>
    </AuthLayout>
  );
}
